import {AfterViewInit, Component, OnChanges, OnInit} from '@angular/core';
import {BaseComponent} from '../../core/base.component';
import {ActivatedRoute} from '@angular/router';
import {HeaderService} from '../../core/services/header.service';
import {Title} from '@angular/platform-browser';
import {AccountService} from '../account.service';
import {AuthService} from '../../core/security/auth.service';
import {AccountSettingsModel} from '../../api/models/account-settings-model';
import { FormArray, FormControl, FormGroup, UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {NotificationService} from '../../notification/notification.service';
import moment from 'moment';
import 'moment-timezone';
import {DateTime} from 'luxon';
import {LookupModel} from '../../api/models/lookup-model';
import {timezoneValidator} from '../../core/validators/timezone.validator';
import {CurrentUser} from '../../api/models/current-user';
import {ModulesEnum} from '../../core/security/modules.enum';
import {HhaSettingsModel} from '../../api/models/hha-settings-model';
import { MatCard, MatCardTitle, MatCardContent } from '@angular/material/card';
import { MatAutocompleteComponent } from '../../editors/mat-autocomplete-input/mat-autocomplete.component';
import { MatNumberInputComponent } from '../../editors/mat-number-input/mat-number-input.component';
import { NgIf } from '@angular/common';
import { FlexModule } from '@angular/flex-layout';
import { MatCheckboxInputComponent } from '../../editors/mat-checkbox-input/mat-checkbox-input.component';
import { UploadFaxComponent } from '../../editors/upload-fax/upload-fax.component';
import { MatTextInputComponent } from '../../editors/mat-text-input/mat-text-input.component';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { DateTimePipe } from '../../core/pipes/date-time.pipe';
import { YesNoPipe } from '../../core/pipes/yesno.pipe';

@Component({
    selector: 'app-profile',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.scss'],
    standalone: true,
    imports: [
        MatCard,
        MatCardTitle,
        MatCardContent,
        FormsModule,
        ReactiveFormsModule,
        MatAutocompleteComponent,
        MatNumberInputComponent,
        NgIf,
        FlexModule,
        MatCheckboxInputComponent,
        UploadFaxComponent,
        MatTextInputComponent,
        MatButton,
        MatTooltip,
        Angular2PromiseButtonModule,
        DateTimePipe,
        YesNoPipe,
    ],
})
export class SettingsComponent extends BaseComponent implements OnInit, OnChanges, AfterViewInit {

  form: UntypedFormGroup;
  submitted: boolean;
  buttonSubmitPromise: Promise<void>;

  settingsModel: AccountSettingsModel = {
    hhaAuth: {} as HhaSettingsModel,
  };
  timeZones: LookupModel[] = [];
  zonesControlTimeZones: LookupModel[] = [];

  currentTime: string;
  previewPdf: string;

  hasHhaModule = false;

  private file1: File;

  constructor(route: ActivatedRoute, headerService: HeaderService, titleService: Title, authService: AuthService,
              private accountService: AccountService,
              private fb: UntypedFormBuilder,
              private notificationService: NotificationService) {
    super(route, headerService, titleService, authService);

    this.timeZones = moment.tz.names().filter(x => this.zoneIsValid(x)).map(x => {
      return {id: x, text: x};
    });
    this.zonesControlTimeZones = this.timeZones.slice();

    this.headerService.showBackButton(true);

    this.createForm();
  }

  ngAfterViewInit() {
    console.log('ngAfterViewInit');
  }

  onCurrentUserChanged(user?: CurrentUser) {
    this.hasHhaModule = this.authService.userHasPackage([ModulesEnum.HhaModule]);
  }

  async ngOnInit() {
    super.ngOnInit();

    this.settingsModel = await this.accountService.getCompanySettings();

    this.ngOnChanges();
  }

  async onSubmit() {
    if (this.form.valid) {
      const reqData = this.prepareFormModel();
      if (reqData.faxHipaEnabled && !this.file1 && !this.settingsModel.faxHipaCoverPageUrl) {
        this.notificationService.notifyDanger('Select HIPAA Cover page file!');
        return;
      }
      this.buttonSubmitPromise = this.processSubmit();
    } else {
      console.error('form is not valid');
      this.markFormControlsAsTouched(this.form);
    }
  }

  markFormControlsAsTouched(formGroup: FormGroup | FormArray): void {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormControl) {
        control.markAsTouched({onlySelf: true});
        control.markAsDirty({onlySelf: true});
        control.updateValueAndValidity();
      } else if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormControlsAsTouched(control);
      }
    });
  }

  async processSubmit(): Promise<void> {
    const reqData = this.prepareFormModel();
    console.log('processSubmit', reqData);

    try {
      this.submitted = true;
      this.settingsModel = await this.accountService.saveCompanySettings({
        timezoneIANA: reqData.timezoneIANA,
        reportingMaxRingTime: reqData.reportingMaxRingTime,
        faxHipaEnabled: reqData.faxHipaEnabled,
        faxHipaCoverPageUrl: this.settingsModel.faxHipaCoverPageUrl,
        file: this.file1,
        hhaAuthString: JSON.stringify(reqData.hhaAuth),
      });

      if (this.settingsModel.faxHipaEnabled && this.settingsModel.faxHipaCoverPageUrl) {
        this.previewPdf = `/api/account/preview-fax-cover/${this.settingsModel.faxHipaCoverPageUrl}`;
      }

      this.ngOnChanges();

      this.notificationService.notifySuccess('Settings updated successfully');
    } catch (e) {
      console.error(e);
    } finally {
      this.submitted = false;
      this.file1 = null;
    }
  }

  ngOnChanges() {
    this.form.reset({
      timezoneIANA: this.settingsModel.timezoneIANA,
      reportingMaxRingTime: this.settingsModel.reportingMaxRingTime,
      faxHipaEnabled: this.settingsModel.faxHipaEnabled,
      hhaAuth: this.settingsModel.hhaAuth
    });

    this.currentTime = this.getLocalTime(this.settingsModel.timezoneIANA);

    if (this.settingsModel.faxHipaEnabled && this.settingsModel.faxHipaCoverPageUrl) {
      this.previewPdf = `/api/account/preview-fax-cover/${this.settingsModel.faxHipaCoverPageUrl}`;
    }
  }

  prepareFormModel(): AccountSettingsModel {
    const formModel = this.form.value;

    return {
      timezoneIANA: formModel.timezoneIANA,
      reportingMaxRingTime: formModel.reportingMaxRingTime,
      faxHipaEnabled: formModel.faxHipaEnabled,
      hhaAuth: formModel.hhaAuth
    };
  }

  createForm(): void {
    const hhaAuthGroup = this.fb.group({
      'apiUrl': [''],
      'appKey': [''],
      'appName': [''],
      'appSecret': [''],
      'useLocalSearch': [false, []],
    }, {
      validators: [this.hhaAuthValidator],
    });

    this.form = this.fb.group({
        'timezoneIANA': ['', [timezoneValidator, Validators.required]],
        'reportingMaxRingTime': [15, [Validators.required]],
        'faxHipaEnabled': [false, []],
        'hhaAuth': hhaAuthGroup,
      },
    );
  }

  onZoneChanged(event) {
    console.log(event);
    this.currentTime = this.getLocalTime(event);
  }

  getLocalTime(timezone: string): string {
    const rezoned = DateTime.local().setZone(timezone);
    return rezoned.toFormat('M/dd/yyyy hh:mm a');
  }

  zoneIsValid(timezone: string): boolean {
    const rezoned = DateTime.local().setZone(timezone);
    return rezoned.isValid;
  }

  onFileValidationError($event: string) {
    this.notificationService.notifyDanger($event);
  }

  onFileChanged1($event: File) {
    console.log('On file 1 changed', $event);
    this.file1 = $event;
  }

  hhaAuthValidator = (group: FormGroup) => {
    if (this.hasHhaModule) {
      const appName = group.get('appName').value;
      const apiUrlControl = group.get('apiUrl');
      const appKeyControl = group.get('appKey');
      const appSecretControl = group.get('appSecret');

      if (appName) {
        if (!apiUrlControl.value) {
          apiUrlControl.setErrors({ 'required': true });
        }
        if (!appKeyControl.value) {
          appKeyControl.setErrors({ 'required': true });
        }
        if (!appSecretControl.value) {
          appSecretControl.setErrors({ 'required': true });
        }
      } else {
        apiUrlControl.setErrors(null);
        appKeyControl.setErrors(null);
        appSecretControl.setErrors(null);
      }
    }
  };
}
